import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import {
    User,
    AuthState,
    TwoFaState,
    TwoFaSetupResponse,
    TwoFaStatusResponse,
    TwoFaMethod,
    TwoFaSetupTotpVerifiedResponse,
} from '../types/auth.types';
import { authApi } from '../services/authApi';
import { tokenService } from '../services/tokenService';
import { apiClient } from '../../../services/api';
import SessionTimeoutModal from './SessionTimeoutModal';
import { env } from '@/config/env';

interface AuthContextType extends AuthState {
    login: (credentials: { email: string; password: string; rememberMe?: boolean }) => Promise<void>;
    logout: () => void;
    signup: (userData: any) => Promise<void>;
    checkAuth: () => Promise<void>;
    // 2FA methods
    verifyTwoFa: (code: string) => Promise<void>;
    resendTwoFaCode: () => Promise<void>;
    setupTwoFa: (method: TwoFaMethod) => Promise<TwoFaSetupResponse>;
    verifyTotpSetup: (code: string) => Promise<TwoFaSetupTotpVerifiedResponse>;
    getTwoFaStatus: () => Promise<TwoFaStatusResponse>;
    disableTwoFa: () => Promise<void>;
    regenerateBackupCodes: () => Promise<string[]>;
    clearTwoFaChallenge: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

const DEFAULT_IDLE_TIMEOUT_MINUTES = 15;
const DEFAULT_WARNING_SECONDS = 15;
const DEFAULT_ABSOLUTE_TIMEOUT_HOURS = 4;
const LEGACY_DEFAULT_SESSION_TIMEOUT_MS = DEFAULT_IDLE_TIMEOUT_MINUTES * 60 * 1000;

const parsePositiveNumber = (rawValue: string | undefined, fallback: number): number => {
    if (!rawValue) {
        return fallback;
    }

    const parsed = Number(rawValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const legacySessionTimeoutMs = parsePositiveNumber(
    String(env.sessionTimeoutMs),
    LEGACY_DEFAULT_SESSION_TIMEOUT_MS
);
const idleTimeoutMinutes = parsePositiveNumber(
    String(env.idleTimeoutMinutes),
    legacySessionTimeoutMs / (60 * 1000)
);
const warningDurationSeconds = parsePositiveNumber(
    String(env.idleWarningSeconds),
    DEFAULT_WARNING_SECONDS
);
const absoluteTimeoutHours = parsePositiveNumber(
    String(env.absoluteTimeoutHours),
    DEFAULT_ABSOLUTE_TIMEOUT_HOURS
);

const IDLE_TIMEOUT_MS = idleTimeoutMinutes * 60 * 1000;
const WARNING_DURATION_SECONDS = Math.max(1, Math.floor(warningDurationSeconds));
const WARNING_DURATION_MS = WARNING_DURATION_SECONDS * 1000;
const ABSOLUTE_TIMEOUT_MS = absoluteTimeoutHours * 60 * 60 * 1000;
const REFRESH_BUFFER_SECONDS = 60;

const STORAGE_KEYS = {
    sessionStartedAt: 'session_started_at',
    lastActivityAt: 'session_last_activity_at',
    logoutBroadcast: 'session_logout_broadcast',
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [state, setState] = useState<AuthState>({
        user: null,
        isAuthenticated: false,
        isLoading: true,
        error: null,
        twoFa: null,
    });
    const [isSessionWarningOpen, setIsSessionWarningOpen] = useState(false);
    const [warningSecondsLeft, setWarningSecondsLeft] = useState(WARNING_DURATION_SECONDS);
    const [isRefreshingSession, setIsRefreshingSession] = useState(false);

    const sessionStartedAtRef = useRef<number | null>(null);
    const lastActivityAtRef = useRef<number | null>(null);
    const warningTimeoutRef = useRef<number | null>(null);
    const idleTimeoutRef = useRef<number | null>(null);
    const absoluteTimeoutRef = useRef<number | null>(null);
    const countdownIntervalRef = useRef<number | null>(null);

    const setLoading = (isLoading: boolean) => {
        setState(prev => ({ ...prev, isLoading }));
    };

    const setError = (error: string | null) => {
        setState(prev => ({ ...prev, error }));
    };

    const setUser = (user: User | null) => {
        setState(prev => ({
            ...prev,
            user,
            isAuthenticated: !!user
        }));
    };

    const setTwoFa = (twoFa: TwoFaState | null) => {
        setState(prev => ({ ...prev, twoFa }));
    };

    const clearTimer = (timerRef: React.MutableRefObject<number | null>) => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const clearIntervalTimer = (timerRef: React.MutableRefObject<number | null>) => {
        if (timerRef.current !== null) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const clearSessionTimers = useCallback(() => {
        clearTimer(warningTimeoutRef);
        clearTimer(idleTimeoutRef);
        clearTimer(absoluteTimeoutRef);
        clearIntervalTimer(countdownIntervalRef);
    }, []);

    const persistSessionStartedAt = (timestamp: number) => {
        sessionStartedAtRef.current = timestamp;
        localStorage.setItem(STORAGE_KEYS.sessionStartedAt, String(timestamp));
    };

    const persistLastActivity = (timestamp: number) => {
        lastActivityAtRef.current = timestamp;
        localStorage.setItem(STORAGE_KEYS.lastActivityAt, String(timestamp));
    };

    const parseTimestamp = (value: string | null): number | null => {
        if (!value) {
            return null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const shouldRefreshAccessToken = () => {
        const token = tokenService.getToken();
        if (!token) {
            return false;
        }

        try {
            const payload = tokenService.decodeToken(token);
            const currentTime = Math.floor(Date.now() / 1000);
            return payload.exp - currentTime <= REFRESH_BUFFER_SECONDS;
        } catch {
            return true;
        }
    };

    const tryRefreshSession = useCallback(async (): Promise<boolean> => {
        const refreshToken = tokenService.getRefreshToken();
        if (!refreshToken) {
            return !tokenService.isTokenExpired();
        }

        try {
            const response = await authApi.refreshToken(refreshToken);
            tokenService.setToken(response.accessToken);
            if (response.refreshToken) {
                tokenService.setRefreshToken(response.refreshToken);
            }
            if (response.user) {
                localStorage.setItem('user', JSON.stringify(response.user));
                setUser(response.user);
            }
            return true;
        } catch {
            return !tokenService.isTokenExpired();
        }
    }, []);

    const performLogout = useCallback((broadcast = true) => {
        clearSessionTimers();
        tokenService.removeTokens();
        localStorage.removeItem('user');
        localStorage.removeItem(STORAGE_KEYS.sessionStartedAt);
        localStorage.removeItem(STORAGE_KEYS.lastActivityAt);
        if (broadcast) {
            localStorage.setItem(STORAGE_KEYS.logoutBroadcast, String(Date.now()));
        }
        sessionStartedAtRef.current = null;
        lastActivityAtRef.current = null;
        setIsSessionWarningOpen(false);
        setWarningSecondsLeft(WARNING_DURATION_SECONDS);
        setIsRefreshingSession(false);
        setUser(null);
        setError(null);
        setTwoFa(null);
    }, [clearSessionTimers]);

    const startWarningCountdown = useCallback((idleDeadline: number) => {
        setIsSessionWarningOpen(true);
        clearIntervalTimer(countdownIntervalRef);

        const updateCountdown = () => {
            const remainingMs = idleDeadline - Date.now();
            const nextSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
            setWarningSecondsLeft(nextSeconds);

            if (nextSeconds <= 0) {
                performLogout();
            }
        };

        updateCountdown();
        countdownIntervalRef.current = window.setInterval(updateCountdown, 1000);
    }, [performLogout]);

    const scheduleSessionTimers = useCallback(() => {
        clearSessionTimers();

        if (!state.isAuthenticated) {
            return;
        }

        const now = Date.now();
        const sessionStartedAt = sessionStartedAtRef.current;
        const lastActivityAt = lastActivityAtRef.current;

        if (!sessionStartedAt || !lastActivityAt) {
            return;
        }

        const absoluteDeadline = sessionStartedAt + ABSOLUTE_TIMEOUT_MS;
        if (absoluteDeadline <= now) {
            performLogout();
            return;
        }

        absoluteTimeoutRef.current = window.setTimeout(() => {
            performLogout();
        }, absoluteDeadline - now);

        const idleDeadline = lastActivityAt + IDLE_TIMEOUT_MS;
        if (idleDeadline <= now) {
            performLogout();
            return;
        }

        const warningAt = idleDeadline - WARNING_DURATION_MS;
        if (warningAt <= now) {
            startWarningCountdown(idleDeadline);
        } else {
            warningTimeoutRef.current = window.setTimeout(() => {
                startWarningCountdown(idleDeadline);
            }, warningAt - now);
        }

        idleTimeoutRef.current = window.setTimeout(() => {
            performLogout();
        }, idleDeadline - now);
    }, [clearSessionTimers, performLogout, startWarningCountdown, state.isAuthenticated]);

    const initializeSessionTracking = useCallback((resetAbsoluteTimeout = false) => {
        const now = Date.now();
        const storedStartedAt = parseTimestamp(localStorage.getItem(STORAGE_KEYS.sessionStartedAt));
        const startedAt = resetAbsoluteTimeout || !storedStartedAt ? now : storedStartedAt;

        persistSessionStartedAt(startedAt);
        persistLastActivity(now);
        setIsSessionWarningOpen(false);
        setWarningSecondsLeft(WARNING_DURATION_SECONDS);
        clearIntervalTimer(countdownIntervalRef);
    }, []);

    const registerActivity = useCallback((broadcast = true) => {
        if (!state.isAuthenticated || isSessionWarningOpen) {
            return;
        }

        const now = Date.now();
        lastActivityAtRef.current = now;
        if (broadcast) {
            localStorage.setItem(STORAGE_KEYS.lastActivityAt, String(now));
        }
        scheduleSessionTimers();
    }, [isSessionWarningOpen, scheduleSessionTimers, state.isAuthenticated]);

    const login = async (credentials: { email: string; password: string; rememberMe?: boolean }) => {
        try {
            setLoading(true);
            setError(null);

            const response = await authApi.login(credentials);

            // Caso 1: Login con 2FA challenge (requiresTwoFa: true)
            if (response.requiresTwoFa) {
                setTwoFa({
                    requiresSetup: false,
                    challengeId: response.challengeId,
                    method: response.twoFaMethod,
                    expiresAt: response.twoFaExpiresAt,
                    tempToken: null,
                });
                // No lanzar error, solo guardar el estado del challenge
                return;
            }

            // Caso 2 y 3: Login exitoso (sin 2FA o SUPER_ADMIN)
            tokenService.setToken(response.accessToken);
            if (response.refreshToken) {
                tokenService.setRefreshToken(response.refreshToken);
            } else {
                localStorage.removeItem('refresh_token');
            }

            // Guardar los datos del usuario en localStorage para persistencia
            localStorage.setItem('user', JSON.stringify(response.user));
            setUser(response.user);
            initializeSessionTracking(true);

            // Verificar si debe configurar 2FA (mensaje específico del backend)
            if (response.needs2faSetup || response.message?.includes('DEBE CONFIGURAR 2FA')) {
                setTwoFa({
                    requiresSetup: true,
                    challengeId: null,
                    method: null,
                    expiresAt: null,
                    tempToken: response.accessToken,
                });
            } else {
                setTwoFa(null);
            }

        } catch (error: any) {
            const rawMessage = String(error.response?.data?.message || error.message || 'Error al iniciar sesión');
            const backendStatus = String(
                error.response?.data?.status || error.response?.data?.userStatus || ''
            ).toUpperCase();
            const shouldMapToEmailVerification =
                backendStatus.includes('PENDING_VERIFICATION') ||
                rawMessage.toLowerCase().includes('deshabilitad');

            const message = shouldMapToEmailVerification
                ? 'Tu correo aún no está verificado. Revisa tu bandeja o solicita un nuevo correo de verificación.'
                : rawMessage;

            setError(message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        performLogout(true);
    };

    const signup = async (userData: any) => {
        try {
            setLoading(true);
            setError(null);

            const response = await authApi.signup(userData);

            tokenService.setToken(response.accessToken);
            if (response.refreshToken) {
                tokenService.setRefreshToken(response.refreshToken);
            }

            // Guardar los datos del usuario en localStorage para persistencia
            localStorage.setItem('user', JSON.stringify(response.user));
            setUser(response.user);
            initializeSessionTracking(true);

        } catch (error: any) {
            const message = error.response?.data?.message || 'Error al registrarse';
            setError(message);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const checkAuth = async () => {
        try {
            const token = tokenService.getToken();

            if (!token || tokenService.isTokenExpired(token)) {
                // Limpiar datos si no hay token válido
                localStorage.removeItem('user');
                setLoading(false);
                return;
            }

            const payload = tokenService.decodeToken(token);
            const requiresTwoFaSetup = Boolean(payload.twoFaSetupRequired);

            try {
                const response = await apiClient.get('/users/me');
                const freshUser = response.data;
                localStorage.setItem('user', JSON.stringify(freshUser));
                setUser(freshUser);
                if (requiresTwoFaSetup) {
                    setTwoFa({
                        requiresSetup: true,
                        challengeId: null,
                        method: null,
                        expiresAt: null,
                        tempToken: token,
                    });
                } else {
                    setTwoFa(null);
                }
                initializeSessionTracking(false);
                return;
            } catch (profileError) {
                console.error('Could not fetch current user profile:', profileError);
            }

            if (requiresTwoFaSetup) {
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                    setUser(JSON.parse(storedUser));
                }
                setTwoFa({
                    requiresSetup: true,
                    challengeId: null,
                    method: null,
                    expiresAt: null,
                    tempToken: token,
                });
                initializeSessionTracking(false);
                return;
            }

            // Si no hay usuario guardado o el token no es válido, intentar refrescar
            const refreshToken = tokenService.getRefreshToken();
            if (refreshToken) {
                try {
                    const response = await authApi.refreshToken(refreshToken);
                    tokenService.setToken(response.accessToken);

                    if (response.refreshToken) {
                        tokenService.setRefreshToken(response.refreshToken);
                    }

                    // Guardar los nuevos datos del usuario
                    localStorage.setItem('user', JSON.stringify(response.user));
                    setUser(response.user);
                    initializeSessionTracking(false);
                } catch (refreshError) {
                    console.error('Refresh token failed:', refreshError);
                    logout();
                }
            } else {
                // No hay refresh token, hacer logout
                logout();
            }

        } catch (error) {
            console.error('Auth check failed:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // 2FA Functions
    // ============================================

    const verifyTwoFa = async (code: string) => {
        try {
            setError(null);

            if (!state.twoFa?.challengeId) {
                throw new Error('No hay un desafío 2FA activo');
            }

            const response = await authApi.verifyTwoFa({
                challengeId: state.twoFa.challengeId,
                code,
            });

            // Login exitoso después de verificar 2FA
            tokenService.setToken(response.accessToken);
            if (response.refreshToken) {
                tokenService.setRefreshToken(response.refreshToken);
            }

            localStorage.setItem('user', JSON.stringify(response.user));
            setUser(response.user);
            setTwoFa(null); // Limpiar estado de 2FA
            initializeSessionTracking(true);

        } catch (error: any) {
            const message = error.response?.data?.message || 'Código inválido';
            setError(message);
            throw error;
        }
    };

    const resendTwoFaCode = async () => {
        try {
            setError(null);

            if (!state.twoFa?.challengeId) {
                throw new Error('No hay un desafío 2FA activo');
            }

            const response = await authApi.resendTwoFaCode(state.twoFa.challengeId);

            setTwoFa({
                requiresSetup: false,
                challengeId: response?.challengeId ?? state.twoFa.challengeId,
                method: response?.twoFaMethod ?? state.twoFa.method,
                expiresAt: response?.twoFaExpiresAt ?? state.twoFa.expiresAt,
                tempToken: null,
            });
        } catch (error: any) {
            const message = error.response?.data?.message || 'Error al reenviar código';
            setError(message);
            throw error;
        }
    };

    const setupTwoFa = async (method: TwoFaMethod): Promise<TwoFaSetupResponse> => {
        try {
            setError(null);

            const response = await authApi.setupTwoFa({ method });
            if ('accessToken' in response && response.accessToken) {
                tokenService.setToken(response.accessToken);
            }
            return response;
        } catch (error: any) {
            const message = error.response?.data?.message || 'Error al configurar 2FA';
            setError(message);
            throw error;
        }
    };

    const verifyTotpSetup = async (code: string): Promise<TwoFaSetupTotpVerifiedResponse> => {
        try {
            setError(null);

            const response = await authApi.verifyTotpSetup({ code });

            // Actualizar estado para indicar que ya configuró 2FA
            if (response.accessToken) {
                tokenService.setToken(response.accessToken);
            }
            return response;
        } catch (error: any) {
            const message = error.response?.data?.message || 'Código TOTP inválido';
            setError(message);
            throw error;
        }
    };

    const getTwoFaStatus = async (): Promise<TwoFaStatusResponse> => {
        try {
            const response = await authApi.getTwoFaStatus();
            return response;
        } catch (error: any) {
            const message = error.response?.data?.message || 'Error al obtener estado 2FA';
            setError(message);
            throw error;
        }
    };

    const disableTwoFa = async () => {
        try {
            setError(null);

            await authApi.disableTwoFa();
        } catch (error: any) {
            const message = error.response?.data?.message || 'Error al desactivar 2FA';
            setError(message);
            throw error;
        }
    };

    const regenerateBackupCodes = async (): Promise<string[]> => {
        try {
            setError(null);

            const response = await authApi.regenerateBackupCodes();
            return response.backupCodes;
        } catch (error: any) {
            const message = error.response?.data?.message || 'Error al regenerar códigos';
            setError(message);
            throw error;
        }
    };

    const clearTwoFaChallenge = () => {
        setTwoFa(null);
        setError(null);
    };

    const handleContinueSession = useCallback(async () => {
        if (!state.isAuthenticated) {
            return;
        }

        setIsRefreshingSession(true);
        try {
            if (shouldRefreshAccessToken()) {
                const refreshed = await tryRefreshSession();
                if (!refreshed) {
                    performLogout();
                    return;
                }
            }

            const now = Date.now();
            persistLastActivity(now);
            setIsSessionWarningOpen(false);
            setWarningSecondsLeft(WARNING_DURATION_SECONDS);
            clearIntervalTimer(countdownIntervalRef);
            scheduleSessionTimers();
        } finally {
            setIsRefreshingSession(false);
        }
    }, [performLogout, scheduleSessionTimers, state.isAuthenticated, tryRefreshSession]);

    const handleStorageEvents = useCallback((event: StorageEvent) => {
        if (!event.key) {
            return;
        }

        if (event.key === STORAGE_KEYS.logoutBroadcast && event.newValue) {
            performLogout(false);
            return;
        }

        if (event.key === STORAGE_KEYS.lastActivityAt && event.newValue && state.isAuthenticated) {
            const externalLastActivityAt = parseTimestamp(event.newValue);
            if (!externalLastActivityAt) {
                return;
            }

            lastActivityAtRef.current = externalLastActivityAt;
            if (isSessionWarningOpen) {
                setIsSessionWarningOpen(false);
                setWarningSecondsLeft(WARNING_DURATION_SECONDS);
                clearIntervalTimer(countdownIntervalRef);
            }
            scheduleSessionTimers();
            return;
        }

        if (event.key === STORAGE_KEYS.sessionStartedAt && event.newValue && state.isAuthenticated) {
            const externalSessionStartedAt = parseTimestamp(event.newValue);
            if (!externalSessionStartedAt) {
                return;
            }

            sessionStartedAtRef.current = externalSessionStartedAt;
            scheduleSessionTimers();
        }
    }, [isSessionWarningOpen, performLogout, scheduleSessionTimers, state.isAuthenticated]);

    useEffect(() => {
        checkAuth();
    }, []);

    useEffect(() => {
        if (!state.isAuthenticated) {
            clearSessionTimers();
            setIsSessionWarningOpen(false);
            setWarningSecondsLeft(WARNING_DURATION_SECONDS);
            return;
        }

        const storedStartedAt = parseTimestamp(localStorage.getItem(STORAGE_KEYS.sessionStartedAt));
        const storedLastActivityAt = parseTimestamp(localStorage.getItem(STORAGE_KEYS.lastActivityAt));
        const now = Date.now();

        if (!storedStartedAt) {
            persistSessionStartedAt(now);
        } else {
            sessionStartedAtRef.current = storedStartedAt;
        }

        if (!storedLastActivityAt) {
            persistLastActivity(now);
        } else {
            lastActivityAtRef.current = storedLastActivityAt;
        }

        scheduleSessionTimers();
    }, [clearSessionTimers, scheduleSessionTimers, state.isAuthenticated]);

    useEffect(() => {
        if (!state.isAuthenticated) {
            return;
        }

        const onUserActivity = () => {
            registerActivity(true);
        };

        const activityEvents: Array<keyof WindowEventMap> = [
            'mousemove',
            'mousedown',
            'keydown',
            'scroll',
            'touchstart',
            'focus',
        ];

        activityEvents.forEach(eventName => {
            window.addEventListener(eventName, onUserActivity, { passive: true });
        });

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                registerActivity(true);
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            activityEvents.forEach(eventName => {
                window.removeEventListener(eventName, onUserActivity);
            });
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [registerActivity, state.isAuthenticated]);

    useEffect(() => {
        window.addEventListener('storage', handleStorageEvents);
        return () => {
            window.removeEventListener('storage', handleStorageEvents);
        };
    }, [handleStorageEvents]);

    useEffect(() => {
        return () => {
            clearSessionTimers();
        };
    }, [clearSessionTimers]);

    const value: AuthContextType = {
        ...state,
        login,
        logout,
        signup,
        checkAuth,
        verifyTwoFa,
        resendTwoFaCode,
        setupTwoFa,
        verifyTotpSetup,
        getTwoFaStatus,
        disableTwoFa,
        regenerateBackupCodes,
        clearTwoFaChallenge,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
            <SessionTimeoutModal
                isOpen={isSessionWarningOpen && state.isAuthenticated}
                secondsRemaining={warningSecondsLeft}
                onContinue={handleContinueSession}
                onLogout={logout}
                isRefreshing={isRefreshingSession}
            />
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
